import type {
  ReminderEngineCriterion,
  ReminderEngineCriterionOperator,
  ReminderEngineDefinition,
  UpsertReminderDefinitionRequest,
  UpsertReminderRuleRequest,
} from '../types/ReminderEngine';

export type CriterionFormState = {
  key: string;
  operator: ReminderEngineCriterionOperator;
  value: string;
};

export type RuleFormState = {
  name: string;
  sortOrder: number;
  offsetValue: number;
  offsetUnit: 'hour' | 'day';
  anchor: 'beforeSubject' | 'onSubject' | 'onCreate';
  criteria: CriterionFormState[];
  channels: Array<{ channelKey: string; templateKey: string; priority: number }>;
};

const parseTiming = (timing: Record<string, unknown>) => {
  const offsetUnit = timing.offsetUnit === 'day' ? 'day' : 'hour';
  const anchor = timing.anchor === 'onSubject'
    ? 'onSubject'
    : timing.anchor === 'onCreate'
      ? 'onCreate'
      : 'beforeSubject';
  const offsetValue = typeof timing.offsetValue === 'number' ? timing.offsetValue : Number(timing.offsetValue ?? 0);

  return {
    offsetValue: Number.isFinite(offsetValue) ? offsetValue : 0,
    offsetUnit,
    anchor,
  } as Pick<RuleFormState, 'offsetValue' | 'offsetUnit' | 'anchor'>;
};

const parseCriterionOperator = (operator: string): ReminderEngineCriterionOperator => {
  switch (operator) {
    case 'notEquals':
      return 'notEquals';
    case 'in':
      return 'in';
    case 'notIn':
      return 'notIn';
    case 'contains':
      return 'contains';
    case 'greaterThan':
      return 'greaterThan';
    case 'lessThan':
      return 'lessThan';
    default:
      return 'equals';
  }
};

const toCriterionFormState = (criteria: ReminderEngineCriterion[]): CriterionFormState[] =>
  criteria.map((criterion) => ({
    key: criterion.key,
    operator: parseCriterionOperator(criterion.operator),
    value: criterion.value,
  }));

export const categoryToSubjectType = (category: string): string => {
  if (category === 'tasks') {
    return 'task';
  }
  if (category === 'calendar') {
    return 'appointment';
  }
  if (category === 'calendar-employee') {
    return 'appointment-staff';
  }
  return '';
};

export const subjectTypeToCategory = (subjectType: string): string => {
  if (subjectType === 'task') {
    return 'tasks';
  }
  if (subjectType === 'appointment') {
    return 'calendar';
  }
  if (subjectType === 'appointment-staff') {
    return 'calendar-employee';
  }
  return subjectType;
};

export const toRuleFormState = (definition: ReminderEngineDefinition): RuleFormState[] =>
  definition.rules
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((rule) => ({
      name: rule.name,
      sortOrder: rule.sortOrder,
      ...parseTiming(rule.timing),
      criteria: toCriterionFormState(rule.criteria),
      channels: rule.channels.map((channel) => ({
        channelKey: channel.channelKey,
        templateKey: channel.templateKey,
        priority: channel.priority,
      })),
    }));

export const emptyCriterion = (): CriterionFormState => ({
  key: '',
  operator: 'equals',
  value: '',
});

export const emptyRule = (
  sortOrder: number,
  defaultChannelKey: string,
  defaultTemplateKey = '',
): RuleFormState => ({
  name: '',
  sortOrder,
  offsetValue: 1,
  offsetUnit: 'hour',
  anchor: 'beforeSubject',
  criteria: [],
  channels: defaultChannelKey && defaultTemplateKey
    ? [{ channelKey: defaultChannelKey, templateKey: defaultTemplateKey, priority: 0 }]
    : [],
});

export const toUpsertPayload = (
  definition: ReminderEngineDefinition,
  name: string,
  category: string,
  rules: RuleFormState[],
): UpsertReminderDefinitionRequest => ({
  id: definition.id,
  key: definition.key,
  name: name.trim(),
  category: category.trim() || null,
  rules: rules.map((rule, index) => ({
    name: rule.name.trim(),
    sortOrder: rule.sortOrder || index + 1,
    timing: {
      mode: 'relative',
      offsetValue: rule.offsetValue,
      offsetUnit: rule.offsetUnit,
      anchor: rule.anchor,
    },
    criteria: rule.criteria
      .filter((criterion) => criterion.key.trim() && criterion.value.trim())
      .map((criterion) => ({
        key: criterion.key.trim(),
        operator: criterion.operator,
        value: criterion.value.trim(),
      })),
    channels: rule.channels
      .filter((channel) => channel.channelKey && channel.templateKey)
      .map((channel) => ({
        channelKey: channel.channelKey,
        templateKey: channel.templateKey.trim(),
        fallback: 'skip',
        priority: channel.priority,
      })),
  } satisfies UpsertReminderRuleRequest)),
});

export const formatRuleTimingSummary = (timing: Record<string, unknown>) => {
  const offsetUnit = timing.offsetUnit === 'day' ? 'day(s)' : 'hour(s)';
  const anchor = timing.anchor === 'onSubject'
    ? 'onSubject'
    : timing.anchor === 'onCreate'
      ? 'onCreate'
      : 'beforeSubject';
  const offsetValue = typeof timing.offsetValue === 'number' ? timing.offsetValue : Number(timing.offsetValue ?? 0);
  if (anchor === 'onCreate' && offsetValue === 0) {
    return 'When created';
  }
  if (anchor === 'onCreate') {
    return `${offsetValue} ${offsetUnit} after creation`;
  }
  if (anchor === 'onSubject' && offsetValue === 0) {
    return 'On subject date';
  }
  if (anchor === 'onSubject') {
    return `${offsetValue} ${offsetUnit} on subject`;
  }
  return `${offsetValue} ${offsetUnit} before subject`;
};

export const formatRuleCriteriaSummary = (criteria: ReminderEngineCriterion[]) => {
  if (criteria.length === 0) {
    return '';
  }

  return criteria
    .map((criterion) => `${criterion.key} ${criterion.operator} ${criterion.value}`)
    .join('; ');
};
